import { bem } from '@nrk/dh-utils'
import format from 'date-fns/format'
import locale from 'date-fns/locale/nb'
import isToday from 'date-fns/is_today'
import Preact from 'preact'
import { Link } from 'preact-router/match'
import {
  IAppState,
  IScheduleMatch,
  IStateMatches,
  ScheduleMatches,
  IScheduleMatches
} from '../../../types'
import { MatchLink } from './MatchLink'
import { formatDate, getNextMatchId, groupMatchesByDate } from './helpers'

import {
  scrollIntoView,
  scrollADistance
} from '../../../lib/scroll-into-view/scroll-into-view'
import * as styles from './index.css'

const { h, Component } = Preact

interface IProps {
  matches?: IScheduleMatch[]
  BASE_URL: string
  currentId: string | false | undefined
  isExternal?: boolean
}

interface IState {
  maxScrollLeft: number
  leftEnd: boolean
  rightEnd: boolean
}

class MatchNav extends Component<IProps, IState> {
  public matchRefs: { [key: string]: HTMLElement } | null = {}
  public containerRef: HTMLUListElement | undefined | null

  private scrollRafId: any

  constructor(props) {
    super(props)
    this.state = {
      maxScrollLeft: 0,
      leftEnd: true,
      rightEnd: true
    }
  }

  public componentWillUnmount() {
    this.matchRefs = null
    this.containerRef = null
    this.stopScrollListener()
    window.removeEventListener('resize', this.handleResize)
  }

  public componentDidMount() {
    this.startScrollListener()
    window.addEventListener('resize', this.handleResize)
    this.handleResize()

    const nextMatchElm: any = this.containerRef.querySelector(
      `.${styles['match-nav__item']}--next`
    )

    if (nextMatchElm) {
      scrollIntoView(nextMatchElm, this.containerRef, {
        duration: 0
      })
    } else {
      this.scrollIntoView(false, 0)
    }
  }

  public componentDidUpdate(prevProps: IProps) {
    const { currentId } = this.props

    if (prevProps.currentId !== currentId) {
      this.scrollIntoView()
    }
  }

  public handleResize = () => {
    if (this.containerRef) {
      const rect = this.containerRef.getBoundingClientRect()
      this.setState({
        maxScrollLeft: this.containerRef.scrollWidth - rect.width
      })
    }
  }

  public startScrollListener() {
    this.scrollRafId = window.requestAnimationFrame(this.tick)
  }

  public stopScrollListener() {
    if (this.scrollRafId) {
      window.cancelAnimationFrame(this.scrollRafId)
      this.scrollRafId = null
    }
  }

  public tick = () => {
    if (this.containerRef) {
      const elm = this.containerRef
      const scrollLeft = elm.scrollLeft
      const leftEnd = scrollLeft === 0
      const rightEnd = scrollLeft === this.state.maxScrollLeft

      if (leftEnd !== this.state.leftEnd || rightEnd !== this.state.rightEnd) {
        this.setState({ leftEnd, rightEnd })
      }
    }

    this.scrollRafId = window.requestAnimationFrame(this.tick)
  }

  public scrollIntoView = (smooth: boolean = true, duration = 250) => {
    const { currentId } = this.props

    if (!currentId) {
      return
    }

    if (
      this.matchRefs &&
      Object.keys(this.matchRefs).length &&
      this.containerRef
    ) {
      let currentRef = null

      if (currentId === undefined) {
        const matchRefKeys = Object.keys(this.matchRefs)
        const firstMatchRef = matchRefKeys && matchRefKeys[0]

        currentRef = this.matchRefs[firstMatchRef]
      } else {
        currentRef = this.matchRefs[currentId]
      }

      if (!currentRef) {
        return
      }

      scrollIntoView(currentRef, this.containerRef, { duration })
    }
  }

  public handleScrollClick = (direction: 'right' | 'left') => () => {
    scrollADistance(this.containerRef, null, { direction })
  }

  public render() {
    const { currentId, isExternal, matches, BASE_URL } = this.props
    const { leftEnd, rightEnd } = this.state
    const nextMatchId = currentId || getNextMatchId(matches)
    const groupedMatches = matches ? groupMatchesByDate(matches) : []

    return (
      <nav
        class={bem(styles['match-nav'], {
          'left-end': leftEnd,
          'right-end': rightEnd
        })}
      >
        <a class="nrk-sr" href="#neste-kamp">
          Hopp til neste kamp som spilles
        </a>
        <ul
          ref={el => (this.containerRef = el)}
          class={styles['match-nav__wrapper']}
        >
          {groupedMatches.reduce((children, group) => {
            return children.concat(
              [
                <li
                  key={format(group.date, 'YYYY-MM-DD')}
                  class={bem(styles['match-nav__item'], 'date', {
                    today: isToday(group.date)
                  })}
                >
                  <span aria-hidden>{formatDate(group.date)}</span>
                  <span class="nrk-sr">
                    {isToday(group.date)
                      ? 'I dag'
                      : format(group.date, 'D. MMMM', { locale })}
                  </span>
                </li>
              ],
              group.matches.map(match => {
                return (
                  <li
                    class={bem(styles['match-nav__item'], 'link', {
                      next: nextMatchId === match.id
                    })}
                    data-href={`${BASE_URL}/kamp?id=${match.id}`}
                    ref={el => (this.matchRefs[match.id] = el)}
                    key={match.id}
                  >
                    <MatchLink
                      BASE_URL={BASE_URL}
                      active={match.id === currentId}
                      match={match}
                      isExternal={isExternal}
                      isNext={nextMatchId === match.id}
                    />
                  </li>
                )
              })
            )
          }, [])}
        </ul>
        <div class={bem(styles['match-nav__page-control'], 'prev')}>
          <button
            disabled={leftEnd}
            onClick={this.handleScrollClick('left')}
            aria-hidden="true"
          >
            <span>
              <svg aria-hidden="true">
                <use xlinkHref="#nrk-chevron-left" />
              </svg>
            </span>
          </button>
        </div>
        <div class={bem(styles['match-nav__page-control'], 'next')}>
          <button
            disabled={rightEnd}
            onClick={this.handleScrollClick('right')}
            aria-hidden="true"
          >
            <span>
              <svg aria-hidden="true">
                <use xlinkHref="#nrk-chevron-right" />
              </svg>
            </span>
          </button>
        </div>
      </nav>
    )
  }
}

export { MatchNav }
